import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
  useDroppable
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  ChevronDownIcon, 
  ChevronRightIcon,
  Bars3Icon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  FolderIcon,
  DocumentIcon,
  CogIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import { toast } from 'react-hot-toast';

interface NavigationItem {
  id: string;
  type: 'module' | 'screen' | 'node';
  module_code?: string;
  screen_code?: string;
  node_code?: string;
  display_name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  children?: NavigationItem[];
  expanded?: boolean;
  parent_id?: string | null;
  module_id?: string;
  screen_id?: string;
  route_path?: string;
  icon?: string;
  level?: number; // Add level for indentation
  category?: 'BUSINESS' | 'ADMIN'; // Add category for modules
}

interface SortableNavigationTreeProps {
  onReorder?: (items: NavigationItem[]) => void;
  onEditItem?: (item: NavigationItem) => void;
}

// Droppable area for modules
const ModuleDroppable: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'module-droppable',
  });

  return (
    <div 
      ref={setNodeRef} 
      className={`min-h-20 p-2 border-2 border-dashed rounded-lg transition-colors ${
        isOver 
          ? 'border-blue-400 bg-blue-50' 
          : 'border-gray-300 bg-gray-50'
      }`}
    >
      {children}
    </div>
  );
};

// Item drop zone - entire item becomes droppable
const ItemDropZone: React.FC<{ 
  item: NavigationItem;
  level: number; 
  isOver: boolean;
  isInsertAbove: boolean;
  isInsertBelow: boolean;
  children: React.ReactNode;
  onDrop: (droppedItemId: string, targetItemId: string, position: 'above' | 'bottom') => void;
}> = ({ item, level, isOver, isInsertAbove, isInsertBelow, children }) => {
  const { setNodeRef } = useDroppable({
    id: `item-drop-${item.id}`,
  });

  return (
    <div
      ref={setNodeRef}
      className="relative"
      style={{ marginLeft: `${level * 24}px` }}
      title={`Drop here to ${item.type === 'module' ? 'add at bottom' : 'add above'} this ${item.type}`}
    >
      {/* Insertion line above - RED for inserting between modules */}
      {isInsertAbove && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 z-50 shadow-lg" style={{ marginTop: '-2px' }} />
      )}
      <div
        className={`transition-all duration-200 ${
          isOver && item.type === 'module' && !isInsertAbove && !isInsertBelow
            ? 'ring-2 ring-blue-500 bg-blue-50' 
            : isOver
            ? 'bg-blue-50'
            : 'hover:bg-gray-50'
        }`}
      >
        {children}
      </div>
      {/* Insertion line below - RED for inserting between modules */}
      {isInsertBelow && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500 z-50 shadow-lg" style={{ marginBottom: '-2px' }} />
      )}
    </div>
  );
};

const SortableItem: React.FC<{
  item: NavigationItem;
  level: number;
  onToggleExpand?: (id: string) => void;
  onToggleActive?: (id: string, isActive: boolean) => void;
  onMoveToParent?: (itemId: string, newParentId: string | null) => void;
  onHardDelete?: (id: string, type: string, name: string) => void;
  onEditItem?: (item: NavigationItem) => void;
  onUpdateSortOrder?: (id: string, type: string, sortOrder: number) => void;
  onToggleCategory?: (id: string, newCategory: 'BUSINESS' | 'ADMIN') => void;
  hoveredDropZone?: string | null;
  insertionPosition?: { itemId: string; position: 'above' | 'below' } | null;
  selectedItems?: Set<string>;
  onItemSelect?: (itemId: string, isSelected: boolean) => void;
}> = ({ item, level: propLevel, onToggleExpand, onToggleActive, onMoveToParent, onHardDelete, onEditItem, onUpdateSortOrder, onToggleCategory, hoveredDropZone, insertionPosition, selectedItems, onItemSelect }) => {
  // Use item.level if available, otherwise use propLevel
  const level = item.level !== undefined ? item.level : propLevel;
  const [isEditingSortOrder, setIsEditingSortOrder] = useState(false);
  const [sortOrderValue, setSortOrderValue] = useState((item.sort_order ?? 0).toString());
  
  // Update sortOrderValue when item.sort_order changes
  useEffect(() => {
    setSortOrderValue((item.sort_order ?? 0).toString());
  }, [item.sort_order]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    data: {
      type: item.type,
      parent_id: item.parent_id,
      module_id: item.module_id
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = item.expanded !== false;
  const isSelected = selectedItems?.has(item.id) || false;

  const handleItemClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      onItemSelect?.(item.id, !isSelected);
    }
  };

  const getIcon = () => {
    switch (item.type) {
      case 'module':
        return <FolderIcon className="w-5 h-5 text-blue-600" />;
      case 'screen':
        return <DocumentIcon className="w-5 h-5 text-green-600" />;
      case 'node':
        return <CogIcon className="w-5 h-5 text-purple-600" />;
      default:
        return <DocumentIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeColor = () => {
    switch (item.type) {
      case 'module':
        return 'bg-blue-100 text-blue-800';
      case 'screen':
        return 'bg-green-100 text-green-800';
      case 'node':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative ${isDragging ? 'opacity-50 z-50' : ''}`}
    >
      <ItemDropZone
        item={item}
        level={level}
        isOver={hoveredDropZone === `item-drop-${item.id}`}
        isInsertAbove={insertionPosition?.itemId === item.id && insertionPosition?.position === 'above'}
        isInsertBelow={insertionPosition?.itemId === item.id && insertionPosition?.position === 'below'}
        onDrop={() => {
          // This will be handled in the main drag end handler
        }}
      >
        <div
          onClick={handleItemClick}
          className={`cursor-pointer relative ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
        >
          {/* Selection Checkbox */}
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-20">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onItemSelect?.(item.id, !isSelected);
              }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
                 <div
           data-item-id={item.id}
           className={`
             flex items-center justify-between p-2 pl-10 border border-gray-200 rounded-lg mb-1
             bg-white hover:bg-gray-50 transition-colors shadow-sm relative
             ${!item.is_active ? 'opacity-60 bg-gray-100' : ''}
             ${level > 0 ? 'border-l-4 border-l-blue-200' : ''}
             ${!item.is_active ? 'border-l-4 border-l-red-300' : ''}
             ${hoveredDropZone === `item-drop-${item.id}` && item.type === 'module' ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
           `}
          style={{ 
            position: 'relative'
          }}
        >
          {/* Connection line for nested items */}
          {level > 0 && (
            <div 
              className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-200"
              style={{ left: '-12px' }}
            />
          )}
          
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded flex-shrink-0"
            >
              <Bars3Icon className="w-4 h-4 text-gray-400" />
            </div>

            {/* Item Icon */}
            <div className="flex-shrink-0">
              {getIcon()}
            </div>

            {/* Expand/Collapse Button */}
            {hasChildren && (
              <button
                onClick={() => onToggleExpand?.(item.id)}
                className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                )}
              </button>
            )}

            {/* Item Type Badge */}
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor()} flex-shrink-0`}>
              {item.type.toUpperCase()}
            </span>

            {/* Category Badge (for modules only) - Clickable to toggle */}
            {item.type === 'module' && item.category && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newCategory = item.category === 'BUSINESS' ? 'ADMIN' : 'BUSINESS';
                  onToggleCategory?.(item.id, newCategory);
                }}
                className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity ${
                  item.category === 'BUSINESS' 
                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' 
                    : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                }`}
                title={`Click to switch to ${item.category === 'BUSINESS' ? 'ADMIN' : 'BUSINESS'} (will update all sub-modules)`}
              >
                {item.category}
              </button>
            )}

            {/* Item Name - Single line with truncation */}
                       <div className="flex-1 min-w-0">
             <div className="flex items-center space-x-2">
               <h3 className="font-medium text-gray-900 truncate" title={item.display_name}>
                 {item.display_name}
               </h3>
               {!item.is_active && (
                 <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                   Inactive
                 </span>
               )}
             </div>
           </div>

            {/* Route path as small badge if present */}
            {item.route_path && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded flex-shrink-0" title={item.route_path}>
                {item.route_path.length > 20 ? `${item.route_path.substring(0, 20)}...` : item.route_path}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {/* Active/Inactive Toggle */}
            <button
              onClick={() => onToggleActive?.(item.id, !item.is_active)}
              className="p-1 hover:bg-gray-100 rounded"
              title={item.is_active ? 'Deactivate' : 'Activate'}
            >
              {item.is_active ? (
                <EyeIcon className="w-4 h-4 text-green-600" />
              ) : (
                <EyeSlashIcon className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {/* Hard Delete */}
            <button
              onClick={() => onHardDelete?.(item.id, item.type, item.display_name)}
              className="p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-800"
              title="Hard Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>

            {/* Edit */}
            <button
              onClick={() => onEditItem?.(item)}
              className="p-1 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-800"
              title="Edit"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>

            {/* Sort Order - Editable */}
            {isEditingSortOrder ? (
              <div className="flex items-center space-x-1">
                <input
                  type="number"
                  value={sortOrderValue}
                  onChange={(e) => setSortOrderValue(e.target.value)}
                  onBlur={() => {
                    const newSortOrder = parseFloat(sortOrderValue);
                    const currentSortOrder = item.sort_order ?? 0;
                    if (!isNaN(newSortOrder) && newSortOrder !== currentSortOrder) {
                      onUpdateSortOrder?.(item.id, item.type, newSortOrder);
                    } else {
                      setSortOrderValue(currentSortOrder.toString());
                    }
                    setIsEditingSortOrder(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const newSortOrder = parseFloat(sortOrderValue);
                      const currentSortOrder = item.sort_order ?? 0;
                      if (!isNaN(newSortOrder) && newSortOrder !== currentSortOrder) {
                        onUpdateSortOrder?.(item.id, item.type, newSortOrder);
                      } else {
                        setSortOrderValue(currentSortOrder.toString());
                      }
                      setIsEditingSortOrder(false);
                    } else if (e.key === 'Escape') {
                      const currentSortOrder = item.sort_order ?? 0;
                      setSortOrderValue(currentSortOrder.toString());
                      setIsEditingSortOrder(false);
                    }
                  }}
                  className="w-16 px-2 py-1 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingSortOrder(true);
                }}
                className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 hover:text-gray-600 transition-colors cursor-pointer"
                title="Click to edit sort order"
              >
                {item.sort_order ?? 0}
              </button>
            )}
          </div>
        </div>
        </div>
      </ItemDropZone>

      {/* Children */}
      {item.type === 'module' && (
        <div className="space-y-1">
          {hasChildren && isExpanded && (
            item.children!.map((child) => (
              <SortableItem
                key={child.id}
                item={child}
                level={level + 1}
                onToggleExpand={onToggleExpand}
                onToggleActive={onToggleActive}
                onMoveToParent={onMoveToParent}
                onHardDelete={onHardDelete}
                onEditItem={onEditItem}
                onUpdateSortOrder={onUpdateSortOrder}
                onToggleCategory={onToggleCategory}
                hoveredDropZone={hoveredDropZone}
                insertionPosition={insertionPosition}
                selectedItems={selectedItems}
                onItemSelect={onItemSelect}
              />
            ))
          )}
        </div>
      )}
      {item.type !== 'module' && hasChildren && isExpanded && (
        <div className="space-y-1">
          {item.children!.map((child) => (
            <SortableItem
              key={child.id}
              item={child}
              level={level + 1}
              onToggleExpand={onToggleExpand}
              onToggleActive={onToggleActive}
              onMoveToParent={onMoveToParent}
              onHardDelete={onHardDelete}
              onEditItem={onEditItem}
              hoveredDropZone={hoveredDropZone}
              insertionPosition={insertionPosition}
              selectedItems={selectedItems}
              onItemSelect={onItemSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const SortableNavigationTree: React.FC<SortableNavigationTreeProps> = ({ onEditItem }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hoveredDropZone, setHoveredDropZone] = useState<string | null>(null);
  const [insertionPosition, setInsertionPosition] = useState<{ itemId: string; position: 'above' | 'below' } | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [mouseY, setMouseY] = useState<number>(0);
  const mouseMoveHandlerRef = React.useRef<((e: MouseEvent) => void) | null>(null);


  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadNavigationHierarchy();
  }, []);

  // Preserve scroll position on component mount
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadNavigationHierarchy = async () => {
    try {
      // Preserve scroll position before loading
      setScrollPosition(window.scrollY);
      
      setLoading(true);
      // Add cache-busting parameter to ensure fresh data
      // const timestamp = Date.now();
      const navigationTree = await unifiedRBACService.getManagementNavigationHierarchy();
      
      // Transform the navigation tree to our internal format
      const transformedItems = transformNavigationTree(navigationTree);
      setItems(transformedItems);
    } catch (error) {
      console.error('Error loading navigation hierarchy:', error);
      toast.error('Failed to load navigation hierarchy');
    } finally {
      setLoading(false);
      
      // Restore scroll position after loading
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  };

  const transformNavigationTree = (navigationTree: any): NavigationItem[] => {
    
    const transformModule = (module: any, level: number = 0): NavigationItem => {
      return {
        id: module.id,
        type: 'module',
        module_code: module.module_code,
        display_name: module.display_name,
        description: module.description,
        sort_order: module.sort_order,
        is_active: module.is_active,
        expanded: true,
        parent_id: module.parent_module_id || null,
        icon: module.icon,
        level: level, // Add level for indentation
        category: module.category || 'BUSINESS', // Include category, default to BUSINESS
        children: module.children ? module.children.map((child: any) => transformChild(child, level + 1)) : []
      };
    };

    const transformScreen = (screen: any, level: number = 0): NavigationItem => {
      return {
        id: screen.id,
        type: 'screen',
        screen_code: screen.screen_code,
        display_name: screen.display_name,
        description: screen.description,
        sort_order: screen.sort_order,
        is_active: screen.is_active,
        expanded: true,
        parent_id: screen.module_id,
        module_id: screen.module_id,
        route_path: screen.route_path,
        icon: screen.icon,
        level: level, // Add level for indentation
        children: [] // Screens don't have nodes in the current structure
      };
    };

    const transformChild = (child: any, level: number = 0): NavigationItem => {
      // Check if child is a screen or a module
      if ('screen_code' in child) {
        // This is a screen
        return transformScreen(child, level);
      } else {
        // This is a module (sub-module)
        return transformModule(child, level);
      }
    };

    // For management interface, show ALL items (active and inactive)
    const allModules = navigationTree.modules.map((module: any) => transformModule(module, 0));
    return allModules;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    // Initialize mouseY with current position
    if (event.activatorEvent && 'clientY' in event.activatorEvent) {
      setMouseY((event.activatorEvent as MouseEvent).clientY);
    }
    // Track mouse position during drag
    const handleMouseMove = (e: MouseEvent) => {
      setMouseY(e.clientY);
    };
    mouseMoveHandlerRef.current = handleMouseMove;
    window.addEventListener('mousemove', handleMouseMove);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over) {
      setHoveredDropZone(null);
      setInsertionPosition(null);
      return;
    }
    
    // Set hovered drop zone for visual feedback
    if (typeof over.id === 'string' && over.id.startsWith('item-drop-')) {
      setHoveredDropZone(over.id);
      
      const targetItemId = over.id.replace('item-drop-', '');
      const targetItem = findItemById(items, targetItemId);
      const activeItem = findItemById(items, active.id as string);
      
      if (targetItem && activeItem && active.id !== targetItemId) {
        // Prevent showing insertion line if trying to move into itself or descendant
        if (isDescendant(activeItem, targetItem)) {
          setInsertionPosition(null);
          return;
        }
        
        // For modules: allow insertion above or below (between modules)
        if (targetItem.type === 'module' && activeItem.type === 'module') {
          // Check if they have the same parent (can reorder at same level)
          if (activeItem.parent_id === targetItem.parent_id) {
            // Determine if we should insert above or below based on mouse position
            // Get the target item's DOM element to find its position
            const targetElement = document.querySelector(`[data-item-id="${targetItemId}"]`);
            
            if (targetElement) {
              const rect = targetElement.getBoundingClientRect();
              const itemHeight = rect.height;
              // For empty modules or when inserting between, use larger tolerance zones
              // Top 40% = insert above (between previous and this module)
              // Bottom 40% = insert below (between this and next module)
              // Middle 20% = move into module
              const topTolerance = Math.min(itemHeight * 0.40, 60);
              const bottomTolerance = Math.min(itemHeight * 0.40, 60);
              const topThreshold = rect.top + topTolerance;
              const bottomThreshold = rect.bottom - bottomTolerance;
              
              // If mouse is in top 40% of module, show insertion line above (insert between previous and this)
              if (mouseY < topThreshold) {
                setInsertionPosition({ itemId: targetItemId, position: 'above' });
              } 
              // If mouse is in bottom 40% of module, show insertion line below (insert between this and next)
              else if (mouseY > bottomThreshold) {
                setInsertionPosition({ itemId: targetItemId, position: 'below' });
              } 
              // If mouse is in middle 20% area, show blue border (move into module) - no insertion line
              else {
                setInsertionPosition(null);
              }
            } else {
              // Fallback: default to above
              setInsertionPosition({ itemId: targetItemId, position: 'above' });
            }
          } else {
            // Different parents: will move into module (no insertion line)
            setInsertionPosition(null);
          }
        } else if (targetItem.type === 'module') {
          // Dragging screen/node into module: no insertion line
          setInsertionPosition(null);
        } else {
          // For screens and other items, show insertion line above
          setInsertionPosition({ itemId: targetItemId, position: 'above' });
        }
      } else {
        setInsertionPosition(null);
      }
    } else {
      setHoveredDropZone(null);
      setInsertionPosition(null);
    }
  };

  // Helper function to cleanup drag state and mouse tracking
  const cleanupDragState = () => {
    setActiveId(null);
    setHoveredDropZone(null);
    setInsertionPosition(null);
    if (mouseMoveHandlerRef.current) {
      window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      mouseMoveHandlerRef.current = null;
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      cleanupDragState();
      return;
    }

    const activeItem = findItemById(items, active.id as string);
    if (!activeItem) {
      cleanupDragState();
      return;
    }

    // Prevent moving item into itself
    if (active.id === over.id) {
      cleanupDragState();
      toast.error('Cannot move an item into itself');
      return;
    }
    
    // Handle different drop scenarios
    if (over.id === 'module-droppable') {
      // Dropping a module at root level
      if (activeItem.type === 'module' && activeItem.parent_id) {
        await handleMoveToParent(activeItem.id, null);
      }
    } else if (typeof over.id === 'string' && over.id.startsWith('item-drop-')) {
      // Dropping on an item - determine position based on item type
      const targetItemId = over.id.replace('item-drop-', '');
      const targetItem = findItemById(items, targetItemId);
      
      if (!targetItem) {
        cleanupDragState();
        return;
      }

      // Prevent moving item into itself or its descendants
      if (isDescendant(activeItem, targetItem)) {
        cleanupDragState();
        toast.error('Cannot move an item into itself or its descendants');
        return;
      }
      
      if (targetItem.type === 'module') {
        if (activeItem.type === 'module') {
          // Check if we're inserting between modules (same parent, with insertion position set)
          const isSameParent = activeItem.parent_id === targetItem.parent_id;
          const hasInsertionPosition = insertionPosition?.itemId === targetItemId;
          
          if (isSameParent) {
            // Same parent - reorder (same logic as screens)
            // Screens just call handleReorder directly when same parent
            if (hasInsertionPosition && insertionPosition.position === 'below') {
              // Insert below target module - find the next sibling after target
              const allSiblings = getSiblings(items, targetItem.parent_id ?? null);
              const siblings = allSiblings.filter(s => s.id !== activeItem.id);
              const targetIndex = siblings.findIndex(s => s.id === targetItem.id);
              
              if (targetIndex >= 0 && targetIndex < siblings.length - 1) {
                // Find the next sibling after target
                const nextSibling = siblings[targetIndex + 1];
                await handleReorder(activeItem.id, nextSibling.id);
              } else {
                // No next sibling - we're moving to the end
                // Recalculate all siblings' sort orders to ensure proper ordering
                const allSiblingsForReorder = getSiblings(items, targetItem.parent_id ?? null);
                const siblingsWithoutActive = allSiblingsForReorder.filter(s => s.id !== activeItem.id);
                const targetIndexForReorder = siblingsWithoutActive.findIndex(s => s.id === targetItem.id);
                
                // Create new order: all items before target, then target, then active item
                const reorderedSiblings = [...siblingsWithoutActive];
                reorderedSiblings.splice(targetIndexForReorder + 1, 0, activeItem);
                
                // Recalculate sort orders for all siblings
                const updatedSiblings = reorderedSiblings.map((item, index) => ({
                  ...item,
                  sort_order: (index + 1) * 10
                }));
                
                // Update all affected items in the backend
                const updatePromises = updatedSiblings.map(sibling => 
                  unifiedRBACService.updateNavigationItem(sibling.id, sibling.type, { sortOrder: sibling.sort_order })
                );
                
                await Promise.all(updatePromises);
                await loadNavigationHierarchy();
              }
            } else {
              // Insert above (default) or no insertion position - same as screens
              await handleReorder(activeItem.id, targetItem.id);
            }
          } else {
            // Different parents - moving a module to become a sub-module of another module
            // Prevent module from setting itself as parent
            if (activeItem.id === targetItem.id) {
              cleanupDragState();
              toast.error('A module cannot be its own parent');
              return;
            }
            await handleMoveToParent(activeItem.id, targetItem.id);
          }
        } else if (activeItem.type === 'screen') {
          // Moving a screen into a module
          await handleMoveToParent(activeItem.id, targetItem.id);
        }
      } else {
        // Drop above the target item (for screens and other items)
        if (activeItem.parent_id === targetItem.parent_id) {
          // Same parent - reorder
          await handleReorder(activeItem.id, targetItem.id);
        } else {
          // Different parent - move to same parent and reorder
          await handleMoveToParent(activeItem.id, targetItem.parent_id || null);
          // Note: The reorder will happen automatically when the hierarchy reloads
        }
      }
    } else {
      // Dropping on another item - check if it's a valid move
      const overItem = findItemById(items, over.id as string);
      if (overItem) {
        // Prevent moving item into itself or its descendants
        if (isDescendant(activeItem, overItem)) {
          cleanupDragState();
          toast.error('Cannot move an item into itself or its descendants');
          return;
        }

        if (activeItem.parent_id === overItem.parent_id) {
          // Same parent - reorder
          await handleReorder(activeItem.id, overItem.id);
        } else if (activeItem.type === 'module' && overItem.type === 'module') {
          // Moving a module to become a sub-module of another module
          // Prevent module from setting itself as parent
          if (activeItem.id === overItem.id) {
            cleanupDragState();
            toast.error('A module cannot be its own parent');
            return;
          }
          await handleMoveToParent(activeItem.id, overItem.id);
        } else if (activeItem.type === 'screen' && overItem.type === 'module') {
          // Moving a screen into a module
          await handleMoveToParent(activeItem.id, overItem.id);
        }
      }
    }

    cleanupDragState();
  };

  const handleMoveToParent = async (itemId: string, newParentId: string | null) => {
    try {
      const item = findItemById(items, itemId);
      if (!item) {
        console.error('Item not found:', itemId);
        return;
      }

      // Prevent a module from setting itself as its own parent
      if (item.type === 'module' && newParentId === itemId) {
        toast.error('A module cannot be its own parent');
        return;
      }

      // Create a copy of the item with updated parent references
      const movedItem: NavigationItem = {
        ...item,
        parent_id: newParentId,
        module_id: item.type === 'screen' ? (newParentId || undefined) : (newParentId || undefined)
      };

      // Optimistically update the UI first
      setItems(currentItems => {
        // First, remove the item from its current location
        const updatedItems = removeItemById(currentItems, itemId);
        
        // Then, add the item to its new location using addItemToParent helper
        return addItemToParent(updatedItems, movedItem, newParentId);
      });

      // Update the item's parent in the backend
      if (item.type === 'module') {
        await unifiedRBACService.updateNavigationItem(itemId, 'module', { parentModuleId: newParentId });
      } else if (item.type === 'screen') {
        await unifiedRBACService.updateNavigationItem(itemId, 'screen', { moduleId: newParentId });
      }

      // Reload hierarchy to ensure consistency with backend
      await loadNavigationHierarchy();

      toast.success(`${item.type} moved successfully`);
    } catch (error) {
      console.error('Error moving item:', error);
      console.error('Error details:', error);
      
      // Revert optimistic update on error
      await loadNavigationHierarchy();
      
      toast.error(`Failed to move item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleReorder = async (activeId: string, overId: string) => {
    try {
      // Find the items and their current sort orders
      const activeItem = findItemById(items, activeId);
      const overItem = findItemById(items, overId);
      
      if (!activeItem || !overItem) {
        console.error('Items not found for reorder:', { activeId, overId });
        return;
      }

      // Get all siblings at the same level
      const siblings = getSiblings(items, activeItem.parent_id ?? null);
      const activeIndex = siblings.findIndex(s => s.id === activeId);
      const overIndex = siblings.findIndex(s => s.id === overId);
      
      if (activeIndex === -1 || overIndex === -1) {
        console.error('Items not found in siblings:', { activeIndex, overIndex, siblings: siblings.map(s => s.id) });
        return;
      }

      // Create new array with reordered items
      const reorderedSiblings = [...siblings];
      const [movedItem] = reorderedSiblings.splice(activeIndex, 1);
      reorderedSiblings.splice(overIndex, 0, movedItem);
      
      // Recalculate sort orders for all siblings (sequential: 10, 20, 30, ...)
      const updatedSiblings = reorderedSiblings.map((item, index) => ({
        ...item,
        sort_order: (index + 1) * 10
      }));

      // Update all affected items in the backend
      const updatePromises = updatedSiblings.map(sibling => 
        unifiedRBACService.updateNavigationItem(sibling.id, sibling.type, { sortOrder: sibling.sort_order })
      );
      
      await Promise.all(updatePromises);

      // Reload hierarchy to ensure consistency with backend
      await loadNavigationHierarchy();

      toast.success('Navigation order updated successfully');
    } catch (error) {
      console.error('Error reordering items:', error);
      
      // Revert optimistic update on error
      await loadNavigationHierarchy();
      
      toast.error(`Failed to update navigation order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };



  const handleToggleExpand = (id: string) => {
    setItems(items => {
      const updateItemExpanded = (items: NavigationItem[]): NavigationItem[] => {
        return items.map(item => {
          if (item.id === id) {
            return { ...item, expanded: !item.expanded };
          }
          if (item.children) {
            return { ...item, children: updateItemExpanded(item.children) };
          }
          return item;
        });
      };
      return updateItemExpanded(items);
    });
  };

  const expandAll = () => {
    setItems(items => {
      const expandAllItems = (items: NavigationItem[]): NavigationItem[] => {
        return items.map(item => {
          const expandedItem = { ...item, expanded: true };
          if (expandedItem.children) {
            expandedItem.children = expandAllItems(expandedItem.children);
          }
          return expandedItem;
        });
      };
      return expandAllItems(items);
    });
  };

  const collapseAll = () => {
    setItems(items => {
      const collapseAllItems = (items: NavigationItem[]): NavigationItem[] => {
        return items.map(item => {
          const collapsedItem = { ...item, expanded: false };
          if (collapsedItem.children) {
            collapsedItem.children = collapseAllItems(collapsedItem.children);
          }
          return collapsedItem;
        });
      };
      return collapseAllItems(items);
    });
  };

  const handleUpdateSortOrder = async (id: string, type: string, sortOrder: number) => {
    try {
      const item = findItemById(items, id);
      if (!item) {
        console.error('Item not found:', id);
        return;
      }

      // Optimistically update the UI
      setItems(currentItems => {
        const updateItemSortOrder = (items: NavigationItem[]): NavigationItem[] => {
          return items.map(item => {
            if (item.id === id) {
              return { ...item, sort_order: sortOrder };
            }
            if (item.children) {
              return { ...item, children: updateItemSortOrder(item.children) };
            }
            return item;
          });
        };
        return updateItemSortOrder(currentItems);
      });

      // Update in backend
      await unifiedRBACService.updateNavigationItem(id, type as 'module' | 'screen', { sortOrder });

      toast.success('Sort order updated successfully');
    } catch (error) {
      console.error('Error updating sort order:', error);
      // Revert optimistic update on error
      await loadNavigationHierarchy();
      toast.error(`Failed to update sort order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      
      const item = findItemById(items, id);
      if (!item) {
        console.error('Item not found:', id);
        return;
      }


      // Update in backend
      await unifiedRBACService.updateNavigationItem(id, item.type, { is_active: isActive });

      // Reload the entire navigation hierarchy to ensure consistency
      await loadNavigationHierarchy();

      toast.success(`${item.type} ${isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling item status:', error);
      toast.error(`Failed to update item status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleToggleCategory = async (id: string, newCategory: 'BUSINESS' | 'ADMIN') => {
    try {
      const item = findItemById(items, id);
      if (!item || item.type !== 'module') {
        console.error('Item not found or not a module:', id);
        return;
      }

      // Confirm action
      const confirmMessage = `Change category to ${newCategory}? This will also update all sub-modules to ${newCategory}.`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Optimistically update the UI
      setItems(currentItems => {
        const updateItemCategory = (items: NavigationItem[]): NavigationItem[] => {
          return items.map(item => {
            if (item.id === id) {
              // Update this module and all its children recursively
              const updateChildren = (children: NavigationItem[]): NavigationItem[] => {
                return children.map(child => {
                  if (child.type === 'module') {
                    return {
                      ...child,
                      category: newCategory,
                      children: child.children ? updateChildren(child.children) : []
                    };
                  }
                  return child;
                });
              };
              return {
                ...item,
                category: newCategory,
                children: item.children ? updateChildren(item.children) : []
              };
            }
            if (item.children) {
              return { ...item, children: updateItemCategory(item.children) };
            }
            return item;
          });
        };
        return updateItemCategory(currentItems);
      });

      // Update in backend with cascade flag
      await unifiedRBACService.updateNavigationItem(id, 'module', { 
        category: newCategory,
        cascadeToChildren: true // Signal backend to cascade to sub-modules
      });

      // Reload the entire navigation hierarchy to ensure consistency
      await loadNavigationHierarchy();

      toast.success(`Module category changed to ${newCategory}. All sub-modules updated.`);
    } catch (error) {
      console.error('Error toggling category:', error);
      // Revert optimistic update on error
      await loadNavigationHierarchy();
      toast.error(`Failed to update category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleHardDelete = async (id: string, type: string, name: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete the ${type} "${name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // Find the item to be deleted
      const itemToDelete = findItemById(items, id);
      if (!itemToDelete) {
        toast.error('Item not found');
        return;
      }

      // Get all children of the item to be deleted
      const childrenToMove = getAllChildren(itemToDelete);

      // Delete from backend
      await unifiedRBACService.deleteNavigationItem(id, type as 'module' | 'screen');

      // Update local state: remove the item and move its children to the parent
      setItems(items => {
        const updatedItems = removeItemById(items, id);
        return moveChildrenToParent(updatedItems, id, childrenToMove);
      });

      toast.success(`${type} "${name}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(`Failed to delete ${type}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleItemSelect = (itemId: string, isSelected: boolean) => {
        setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const handleBulkMove = async (targetParentId: string | null) => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      // Optimistically update the UI
      setItems(currentItems => {
        let updatedItems = [...currentItems];
        
        selectedItems.forEach(itemId => {
          const item = findItemById(updatedItems, itemId);
          if (item) {
            // Check if we're moving to the same parent (no-op)
            if (item.parent_id === targetParentId) {
              return; // Skip if already in target parent
            }
            
            // Remove from current location first
            updatedItems = removeItemById(updatedItems, itemId);
            
            // Create updated item with new parent references
            const updatedItem = {
              ...item,
              parent_id: targetParentId,
              module_id: targetParentId || undefined
            };
            
            // Add to new location
            updatedItems = addItemToParent(updatedItems, updatedItem, targetParentId);
          }
        });
        
        return updatedItems;
      });

      // Update backend for each selected item
      const updatePromises = Array.from(selectedItems).map(async (itemId) => {
        const item = findItemById(items, itemId);
        if (item) {
          // Skip if already in target parent
          if (item.parent_id === targetParentId) {
            return;
          }
          
          if (item.type === 'module') {
            await unifiedRBACService.updateNavigationItem(itemId, 'module', { parentModuleId: targetParentId });
          } else if (item.type === 'screen') {
            await unifiedRBACService.updateNavigationItem(itemId, 'screen', { moduleId: targetParentId });
          }
        }
      });

      await Promise.all(updatePromises);
      
      // Clear selection
      setSelectedItems(new Set());
      
      toast.success(`${selectedItems.size} items moved successfully`);
    } catch (error) {
      console.error('Error in bulk move:', error);
      // Revert optimistic update
      await loadNavigationHierarchy();
      toast.error('Failed to move items');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    const itemNames = Array.from(selectedItems).map(id => {
      const item = findItemById(items, id);
      return item?.display_name || 'Unknown item';
    }).join(', ');

    if (!window.confirm(`Are you sure you want to permanently delete ${selectedItems.size} items?\n\nItems: ${itemNames}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      // Optimistically update the UI
      setItems(currentItems => {
        let updatedItems = [...currentItems];
        selectedItems.forEach(itemId => {
          const item = findItemById(updatedItems, itemId);
          if (item) {
            const childrenToMove = getAllChildren(item);
            updatedItems = removeItemById(updatedItems, itemId);
            updatedItems = moveChildrenToParent(updatedItems, itemId, childrenToMove);
          }
        });
        return updatedItems;
      });

      // Delete from backend
      const deletePromises = Array.from(selectedItems).map(async (itemId) => {
        const item = findItemById(items, itemId);
        if (item) {
          await unifiedRBACService.deleteNavigationItem(itemId, item.type as 'module' | 'screen');
        }
      });

      await Promise.all(deletePromises);
      
      // Clear selection
      setSelectedItems(new Set());
      
      toast.success(`${selectedItems.size} items deleted successfully`);
    } catch (error) {
      console.error('Error in bulk delete:', error);
      // Revert optimistic update
      await loadNavigationHierarchy();
      toast.error('Failed to delete items');
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const findItemById = (items: NavigationItem[], id: string): NavigationItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to check if an item is a descendant of another item
  // Prevents moving an item into itself or its descendants
  const isDescendant = (ancestor: NavigationItem, potentialDescendant: NavigationItem): boolean => {
    if (!ancestor.children || ancestor.children.length === 0) return false;
    
    for (const child of ancestor.children) {
      if (child.id === potentialDescendant.id) return true;
      if (isDescendant(child, potentialDescendant)) return true;
    }
    return false;
  };

  const updateItemById = (items: NavigationItem[], id: string, updates: Partial<NavigationItem>): NavigationItem[] => {
    return items.map(item => {
      if (item.id === id) {
        return { ...item, ...updates };
      }
      if (item.children) {
        return { ...item, children: updateItemById(item.children, id, updates) };
      }
      return item;
    });
  };

  const removeItemById = (items: NavigationItem[], id: string): NavigationItem[] => {
    return items.filter(item => {
      if (item.id === id) {
        return false;
      }
      if (item.children) {
        item.children = removeItemById(item.children, id);
      }
      return true;
    });
  };

  const getAllChildren = (item: NavigationItem): NavigationItem[] => {
    const children: NavigationItem[] = [];
    if (item.children) {
      item.children.forEach(child => {
        children.push(child);
        children.push(...getAllChildren(child));
      });
    }
    return children;
  };

  const moveChildrenToParent = (items: NavigationItem[], deletedItemId: string, childrenToMove: NavigationItem[]): NavigationItem[] => {
    return items.map(item => {
      if (item.children) {
        // Check if this item has the deleted item as a child
        const hasDeletedChild = item.children.some(child => child.id === deletedItemId);
        
        if (hasDeletedChild) {
          // Remove the deleted item and add its children to this parent
          const updatedChildren = item.children.filter(child => child.id !== deletedItemId);
          return {
            ...item,
            children: [...updatedChildren, ...childrenToMove]
          };
        } else {
          // Recursively check children
          return {
            ...item,
            children: moveChildrenToParent(item.children, deletedItemId, childrenToMove)
          };
        }
      }
      return item;
    });
  };

  // Helper function to get all siblings of an item (items with the same parent), sorted by sort_order
  const getSiblings = (items: NavigationItem[], parentId: string | null): NavigationItem[] => {
    const flattenItems = (items: NavigationItem[]): NavigationItem[] => {
      const result: NavigationItem[] = [];
      for (const item of items) {
        if (item.parent_id === parentId) {
          result.push(item);
        }
        if (item.children) {
          result.push(...flattenItems(item.children));
        }
      }
      return result;
    };
    
    const siblings = flattenItems(items);
    // Sort by sort_order
    return siblings.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  };

  const addItemToParent = (items: NavigationItem[], item: NavigationItem, parentId: string | null): NavigationItem[] => {
    // If adding to root level (parentId === null), add directly to the root array
    if (parentId === null) {
      // Check if item already exists in root array
      const existingIndex = items.findIndex(existingItem => existingItem.id === item.id);
      if (existingIndex >= 0) {
        // Item already exists, update it instead of duplicating
        const updatedItems = [...items];
        updatedItems[existingIndex] = item;
        return updatedItems;
      }
      // Add new item to root array
      return [...items, item];
    }
    
    // Otherwise, find the parent and add to its children
    return items.map(currentItem => {
      if (currentItem.id === parentId) {
        // Add to this module - ensure no duplicates
        const existingIndex = currentItem.children?.findIndex(child => child.id === item.id);
        if (existingIndex !== undefined && existingIndex >= 0) {
          // Item already exists, update it instead of duplicating
          const updatedChildren = [...(currentItem.children || [])];
          updatedChildren[existingIndex] = item;
          return {
            ...currentItem,
            children: updatedChildren
          };
        }
        return {
          ...currentItem,
          children: [...(currentItem.children || []), item]
        };
      } else if (currentItem.children) {
        return {
          ...currentItem,
          children: addItemToParent(currentItem.children, item, parentId)
        };
      }
      return currentItem;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading navigation hierarchy...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('navigation.dragAndDropReorder')}
          </h3>
          {/* Expand/Collapse All Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={expandAll}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              title="Expand all items"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              title="Collapse all items"
            >
              Collapse All
            </button>
          </div>
          {/* Select All Checkbox */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={items.length > 0 && selectedItems.size === items.length}
              ref={(input) => {
                if (input) input.indeterminate = selectedItems.size > 0 && selectedItems.size < items.length;
              }}
              onChange={(e) => {
                if (e.target.checked) {
                  // Select all items
                  const allItemIds = new Set<string>();
                  const collectAllIds = (items: NavigationItem[]) => {
                    items.forEach(item => {
                      allItemIds.add(item.id);
                      if (item.children) {
                        collectAllIds(item.children);
                      }
                    });
                  };
                  collectAllIds(items);
                  setSelectedItems(allItemIds);
                } else {
                  // Deselect all
                  setSelectedItems(new Set());
                }
              }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-600">Select All</span>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          <p>Drag and drop to reorder and restructure navigation items</p>
          <p className="text-xs mt-1">
            💡 <strong>Tips:</strong> Use checkboxes to select items • Click folder icons to expand/collapse • Drop items on modules to add at bottom • Drop items on screens to add above
          </p>
        </div>
      </div>

      {/* Bulk Operations Toolbar */}
      {selectedItems.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkDelete()}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                title="Delete selected items"
              >
                Delete Selected
              </button>
              <button
                onClick={() => handleBulkMove(null)}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                title="Move selected items to root level"
              >
                Move to Root
              </button>
            </div>
          </div>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <ModuleDroppable>
          <div className="space-y-1 flex flex-col">
            {items.map((item) => (
              <SortableItem
                key={item.id}
                item={item}
                level={0}
                onToggleExpand={handleToggleExpand}
                onToggleActive={handleToggleActive}
                onMoveToParent={handleMoveToParent}
                onHardDelete={handleHardDelete}
                onEditItem={onEditItem}
                onUpdateSortOrder={handleUpdateSortOrder}
                onToggleCategory={handleToggleCategory}
                hoveredDropZone={hoveredDropZone}
                insertionPosition={insertionPosition}
                selectedItems={selectedItems}
                onItemSelect={handleItemSelect}
              />
            ))}
          </div>
        </ModuleDroppable>

        <DragOverlay>
          {activeId ? (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
              <div className="flex items-center space-x-3">
                <Bars3Icon className="w-4 h-4 text-gray-400" />
                <span className="font-medium">
                  {findItemById(items, activeId)?.display_name}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
